import { useState, useRef } from 'react';
import { Upload, Film, Link2, X, CheckCircle, Loader2, AlertTriangle, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { uploadFile } from 'zite-file-upload-sdk';
import { saveModuleVideo } from 'zite-endpoints-sdk';
import { toast } from 'sonner';

const MAX_SIZE_MB = 25;
const ACCEPTED_TYPES = ['video/mp4', 'video/webm'];
const BLOCKED_PATTERNS = ['heygen.com', 'avatar_tmp', '/share/', 'app.heygen'];

export default function VideoUploader({ moduleId, currentUrl, moduleLabel, onSaved }: {
  moduleId: string;
  currentUrl: string;
  moduleLabel: string;
  onSaved: (url: string) => void;
}) {
  const [mode, setMode] = useState<'idle' | 'upload' | 'paste'>('idle');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [pasteUrl, setPasteUrl] = useState('');
  const [error, setError] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const handleFile = async (file: File) => {
    setError('');
    if (!ACCEPTED_TYPES.includes(file.type)) {
      setError(`Invalid file type: ${file.type}. Only MP4 and WebM are accepted.`);
      return;
    }
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      setError(`File too large: ${(file.size / 1024 / 1024).toFixed(1)}MB. Maximum is ${MAX_SIZE_MB}MB.`);
      return;
    }
    setUploading(true);
    setProgress(10);
    try {
      // Simulate progress during upload
      const progressTimer = setInterval(() => {
        setProgress(p => Math.min(p + 8, 85));
      }, 500);
      const { fileUrl } = await uploadFile({ data: file, filename: `module-video-${moduleId.slice(0, 8)}-${file.name}` });
      clearInterval(progressTimer);
      setProgress(90);
      // Save to module
      const res = await saveModuleVideo({ moduleId, videoUrl: fileUrl, source: 'upload' });
      setProgress(100);
      if (res.success) {
        toast.success(`Video uploaded for ${moduleLabel}`);
        onSaved(fileUrl);
        setMode('idle');
      }
    } catch (err: any) {
      setError(err?.message || 'Upload failed');
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const handlePaste = async () => {
    setError('');
    const url = pasteUrl.trim();
    if (!url) return;
    for (const pattern of BLOCKED_PATTERNS) {
      if (url.toLowerCase().includes(pattern)) {
        setError(`Rejected: URL contains "${pattern}". Only stable, directly-playable HTTPS video URLs or YouTube links are accepted.`);
        return;
      }
    }
    if (!url.startsWith('https://')) {
      setError('URL must start with https://');
      return;
    }
    setUploading(true);
    try {
      const res = await saveModuleVideo({ moduleId, videoUrl: url, source: 'paste' });
      if (res.success) {
        toast.success(`Video URL saved for ${moduleLabel}`);
        onSaved(url);
        setMode('idle');
        setPasteUrl('');
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to save URL');
    } finally {
      setUploading(false);
    }
  };

  const isYouTube = (url: string) => /youtube\.com|youtu\.be/.test(url);

  return (
    <div className="space-y-3">
      {/* Current video status */}
      {currentUrl ? (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-primary/5 border border-primary/20">
          <CheckCircle className="w-4 h-4 text-primary shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-foreground">Video assigned</p>
            <p className="text-xs text-muted-foreground truncate">{currentUrl}</p>
          </div>
          {isYouTube(currentUrl) ? (
            <a href={currentUrl} target="_blank" rel="noopener noreferrer" className="shrink-0">
              <ExternalLink className="w-4 h-4 text-muted-foreground hover:text-primary" />
            </a>
          ) : (
            <video src={currentUrl} className="w-20 h-12 rounded object-cover shrink-0" controls={false} muted preload="metadata" />
          )}
        </div>
      ) : (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/50 border border-border">
          <Film className="w-4 h-4 text-muted-foreground" />
          <p className="text-xs text-muted-foreground">No video assigned</p>
        </div>
      )}

      {mode === 'idle' && (
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setMode('upload')} className="flex-1">
            <Upload className="w-4 h-4 mr-1" /> Upload MP4/WebM
          </Button>
          <Button variant="outline" size="sm" onClick={() => setMode('paste')} className="flex-1">
            <Link2 className="w-4 h-4 mr-1" /> Paste URL
          </Button>
        </div>
      )}

      {mode === 'upload' && (
        <div className="space-y-2">
          <input ref={fileRef} type="file" accept="video/mp4,video/webm" className="hidden"
            onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f); }} />
          <Button variant="outline" className="w-full h-20 border-dashed" disabled={uploading}
            onClick={() => fileRef.current?.click()}>
            {uploading ? (
              <div className="flex flex-col items-center gap-1">
                <Loader2 className="w-5 h-5 animate-spin text-primary" />
                <span className="text-xs">Uploading… {progress}%</span>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-1 text-muted-foreground">
                <Upload className="w-5 h-5" />
                <span className="text-xs">Click to select MP4 or WebM (max {MAX_SIZE_MB}MB)</span>
              </div>
            )}
          </Button>
          {uploading && (
            <div className="w-full bg-muted rounded-full h-2">
              <div className="bg-primary h-2 rounded-full transition-all" style={{ width: `${progress}%` }} />
            </div>
          )}
          <Button variant="ghost" size="sm" onClick={() => setMode('idle')} disabled={uploading}>
            <X className="w-3 h-3 mr-1" /> Cancel
          </Button>
        </div>
      )}

      {mode === 'paste' && (
        <div className="space-y-2">
          <div className="flex gap-2">
            <Input placeholder="https://youtube.com/watch?v=... or https://.../video.mp4" value={pasteUrl}
              onChange={e => setPasteUrl(e.target.value)} className="flex-1 text-xs" disabled={uploading} />
            <Button size="sm" onClick={handlePaste} disabled={uploading || !pasteUrl.trim()}>
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
            </Button>
          </div>
          <p className="text-[10px] text-muted-foreground">Accepts YouTube URLs or direct HTTPS links to MP4/WebM files. HeyGen dashboard/share URLs are blocked.</p>
          <Button variant="ghost" size="sm" onClick={() => { setMode('idle'); setPasteUrl(''); }} disabled={uploading}>
            <X className="w-3 h-3 mr-1" /> Cancel
          </Button>
        </div>
      )}

      {error && (
        <div className="flex items-start gap-2 p-2 rounded-lg bg-destructive/10 border border-destructive/20">
          <AlertTriangle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
          <p className="text-xs text-destructive">{error}</p>
        </div>
      )}
    </div>
  );
}
